'use client';

import Box from '@mui/material/Box';
import { animated, useSpring } from 'react-spring';
import { GRUND, djup, hexTillRgba, ljus, type Kombonamn } from './rörelse-konfig';
import { useRörelse } from './RörelseKontext';
import type { IFärgKombo } from 'src/theme/NyttFärgtema';

const AnimeradDiv = animated('div');

type Props = {
    kombo: Kombonamn | IFärgKombo;
    etikett?: string;
    storlek?: number;
    /** Släckt nod ritas dämpad men fullt synlig — aldrig osynlig. */
    tänd?: boolean;
};

/**
 * Accentnod med pulserande ring.
 *
 * Läser rörelseinställningen själv i stället för att lita på anroparen: en oändlig loop
 * är precis den sorts rörelse folk stänger av, och `skipAnimation` uttrycker ingen avsikt
 * om just loopar.
 */
export function Pulsnod({ kombo, etikett, storlek = 14, tänd = true }: Props) {
    const { mindreRörelse } = useRörelse();
    const loopar = tänd && !mindreRörelse;

    const ring = useSpring({
        from: { scale: 1, opacity: 0.5 },
        to: { scale: 2.4, opacity: 0 },
        loop: loopar,
        config: { duration: 1800 },
        immediate: !loopar,
        reset: loopar
    });

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ position: 'relative', width: storlek, height: storlek, flexShrink: 0 }}>
                <AnimeradDiv
                    style={{
                        ...ring,
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        border: `1px solid ${ljus(kombo)}`
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        bgcolor: tänd ? ljus(kombo) : 'transparent',
                        border: tänd ? 'none' : `1px solid ${hexTillRgba(ljus(kombo), 0.45)}`,
                        boxShadow: tänd ? `0 0 14px 2px ${hexTillRgba(djup(kombo), 0.9)}` : 'none'
                    }}
                />
            </Box>
            {etikett ? (
                <Box
                    component="span"
                    sx={{
                        fontSize: 14,
                        letterSpacing: '0.01em',
                        color: tänd ? GRUND.text : GRUND.dämpad
                    }}
                >
                    {etikett}
                </Box>
            ) : null}
        </Box>
    );
}
